import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import roleService from '../services/roleService';

const ProtectedRoute = ({ children, allowedRoles = null }) => {
  const { user, userProfile, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [roleLoading, setRoleLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [accessValidated, setAccessValidated] = useState(false);
  const hasNavigatedRef = useRef(false);
  
  // Create stable reference for allowedRoles to prevent unnecessary re-renders
  const allowedRolesKey = useMemo(() => {
    if (!allowedRoles || !Array.isArray(allowedRoles)) return null;
    return JSON.stringify(allowedRoles?.sort());
  }, [allowedRoles]);
  
  // Get stable user ID
  const userId = user?.id || null;
  
  // Track previous values to prevent unnecessary re-validation
  const prevUserIdRef = useRef(null);
  const prevAllowedRolesKeyRef = useRef(null);
  const prevLoadingRef = useRef(true);
  const validationInProgressRef = useRef(false);
  const hasValidatedRef = useRef(false);

  // Fetch and validate user role from database
  useEffect(() => {
    // Skip if already validating
    if (validationInProgressRef?.current) return;
    
    // Reset navigation flag when route changes
    const currentPath = location?.pathname;
    if (currentPath !== '/login-authentication' && currentPath !== '/admin-dashboard' && currentPath !== '/employee-dashboard') {
      hasNavigatedRef.current = false;
    }
    
    // Check if loading just finished (changed from true to false)
    const loadingFinished = prevLoadingRef?.current === true && loading === false;
    
    // Check if user ID or allowedRoles actually changed
    const userIdChanged = userId !== prevUserIdRef?.current;
    const allowedRolesChanged = allowedRolesKey !== prevAllowedRolesKeyRef?.current;
    
    // Skip validation if nothing changed and we've already validated before
    if (!loadingFinished && !userIdChanged && !allowedRolesChanged && hasValidatedRef?.current) {
      prevLoadingRef.current = loading;
      return; // No actual change, skip validation
    }
    
    // Update loading ref
    prevLoadingRef.current = loading;
    
    const validateAccess = async () => {
      if (loading) {
        setRoleLoading(true);
        return; // Wait for auth to load
      }
      
      if (!user || !userId) {
        prevUserIdRef.current = null;
        prevAllowedRolesKeyRef.current = allowedRolesKey;
        setRoleLoading(false);
        setUserRole(null);
        setAccessValidated(false);
        validationInProgressRef.current = false;
        hasValidatedRef.current = false;
        return;
      }

      // Prevent concurrent validations
      if (validationInProgressRef?.current) return;
      
      validationInProgressRef.current = true;
      setRoleLoading(true);

      try {
        // Get role from database (authoritative source)
        const currentRole = await roleService?.getCurrentUserRole();
        
        // Only update state if role actually changed
        setUserRole(prev => {
          if (prev !== currentRole) {
            return currentRole;
          }
          return prev;
        });

        // Validate access if specific roles are required
        if (allowedRoles && allowedRoles?.length > 0) {
          const hasAccess = await roleService?.hasPermission(allowedRoles);
          setAccessValidated(prev => {
            if (prev !== hasAccess) {
              return hasAccess;
            }
            return prev;
          });
        } else {
          setAccessValidated(true); // No specific role required
        }
        
        // Update refs after successful validation
        prevUserIdRef.current = userId;
        prevAllowedRolesKeyRef.current = allowedRolesKey;
        hasValidatedRef.current = true;
      } catch (error) {
        console.error('Error validating role access:', error);
        setUserRole('employee'); // Default role on error
        setAccessValidated(false);
        prevUserIdRef.current = userId;
        prevAllowedRolesKeyRef.current = allowedRolesKey;
        hasValidatedRef.current = true;
      } finally {
        setRoleLoading(false);
        validationInProgressRef.current = false;
      }
    };

    validateAccess();
  }, [user, userId, loading, allowedRolesKey]);

  // Reset navigation flag when route changes (separate effect to avoid loops)
  useEffect(() => {
    const currentPath = location?.pathname;
    // Reset flag when we reach a valid route and user is authenticated
    if (user && userId) {
      // Reset flag when we're on a valid dashboard route
      if (currentPath === '/admin-dashboard' || currentPath === '/employee-dashboard') {
        hasNavigatedRef.current = false;
      }
      // Also reset if we're on the exact route we should be on (for protected routes)
      if (allowedRoles && allowedRoles?.length > 0 && accessValidated) {
        hasNavigatedRef.current = false;
      }
    } else if (currentPath === '/login-authentication') {
      // Reset flag when on login page
      hasNavigatedRef.current = false;
    }
  }, [location?.pathname, user, userId, allowedRoles, accessValidated]);

  // Handle navigation separately to prevent infinite loops
  useEffect(() => {
    if (loading || roleLoading) return;

    const currentPath = location?.pathname;

    // Redirect to login if not authenticated
    if (!user || !userId) {
      if (currentPath !== '/login-authentication' && !hasNavigatedRef?.current) {
        hasNavigatedRef.current = true;
        navigate('/login-authentication', { replace: true });
      }
      return;
    }

    // If specific roles are required, check and redirect if needed
    if (allowedRoles && allowedRoles?.length > 0 && userRole) {
      if (!accessValidated) {
        // User doesn't have access to this route
        // Check if we're already on the correct route for their role
        const redirectPath = getRedirectPath(userRole);
        const isOnCorrectRoute = currentPath === redirectPath;
        
        // Only redirect if we're NOT on the correct route for their role
        if (!isOnCorrectRoute && !hasNavigatedRef?.current) {
          hasNavigatedRef.current = true;
          navigate(redirectPath, { replace: true });
        } else if (isOnCorrectRoute) {
          // We're already on the correct route, reset flag and let validation complete
          hasNavigatedRef.current = false;
        }
      } else {
        // User has access, reset navigation flag
        hasNavigatedRef.current = false;
      }
    } else {
      // No specific roles required, reset navigation flag
      hasNavigatedRef.current = false;
    }
  }, [user, userId, loading, roleLoading, userRole, accessValidated, allowedRoles, navigate, location?.pathname]);

  // Show loading while checking authentication and roles
  if (loading || roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Show loading if we need to validate role but haven't yet
  if (allowedRoles && allowedRoles?.length > 0 && !userRole && !hasValidatedRef?.current) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verifying role access...</p>
        </div>
      </div>
    );
  }

  // Don't render children if user is not authenticated (show loading while navigating)
  if (!user || !userId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  // Render children if:
  // 1. No role restrictions (open route)
  // 2. Role validated and access granted
  if (!allowedRoles || allowedRoles?.length === 0) {
    // No role restrictions, render children
    return children;
  }

  // Protected route with role requirements
  if (!userRole) {
    // Still validating role, show loading
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verifying role access...</p>
        </div>
      </div>
    );
  }
  
  // Check if user has access
  if (accessValidated) {
    // Access granted, render children
    return children;
  }
  
  // Access not validated yet or access denied
  // Check if we're on the correct route for this user's role
  const correctRouteForRole = getRedirectPath(userRole);
  const isOnCorrectRoute = location?.pathname === correctRouteForRole;
  
  if (isOnCorrectRoute) {
    // We're on the correct route, validation might still be in progress
    // Give it a moment, then render children (optimistic rendering)
    // This handles the case where validation completes but state hasn't updated yet
    return children;
  }
  
  // Access denied and on wrong route, show loading while navigation redirects
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Redirecting...</p>
      </div>
    </div>
  );
};

// Helper function to determine redirect path based on database role
const getRedirectPath = (role) => {
  switch (role) {
    case 'admin': case'hr_manager':
      return '/admin-dashboard';
    case 'employee': case'supervisor':
    default:
      return '/employee-dashboard';
  }
};

export default ProtectedRoute;